import { AuthContext, CommonContext, snakify } from '@repo-level/core-front';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export const INTERCOM_APP_ID = 'ozlaecfj';

interface IntercomContextActions {
  boot: () => void;
  show: () => void;
  onHide: (callback: () => void) => void;
  offHide: (callback: () => void) => void;
}

export type VerifiedEmail = 'Yes' | 'No';

export interface IntercomUpdateBody {
  name: string | null;
  email: string;
  phone: string | null;
  groups: string | null;
  dateOfBirth: string | null;
  userId: string | null;
  createdAt: string | null;
  company: {
    companyId: string;
    name: string;
  };
  verifiedEmail: VerifiedEmail;
  countryByIp: string | null;
  profileCountry: string | null;
  gender: string | null;
}

const COMPANY = {
  companyId: '1',
  name: 'LE',
};

export const IntercomContext = createContext<IntercomContextActions>(
  null as any
);

export const IntercomContextProvider: FC = ({ children }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { currentIp } = useContext(CommonContext);

  const onHideHandlersRef = useRef<Set<() => void>>(new Set());
  const [isOnHideBound, setIsOnHideBound] = useState(false);

  const boot = useCallback(() => {
    try {
      window.Intercom?.(
        'boot',
        snakify({
          appId: INTERCOM_APP_ID,
          hideDefaultLauncher: true,
          customLauncherSelector: '#intercomcustomLauncher',
          company: COMPANY,
          casino: COMPANY.name,
        })
      );

      // bind Intercom onHide ONCE
      if (!isOnHideBound) {
        window.Intercom?.('onHide', () => {
          onHideHandlersRef.current.forEach((cb) => {
            try {
              cb();
            } catch (err) {
              console.error('Error in Intercom onHide handler:', err);
            }
          });
        });
        setIsOnHideBound(true);
      }
    } catch (err) {
      console.error('Intercom boot failed', err);
    }
  }, [isOnHideBound]);

  const update = useCallback((body: IntercomUpdateBody) => {
    window.Intercom?.('update', snakify(body));
  }, []);

  const show = useCallback(() => {
    window.Intercom?.('show');
  }, []);

  // Register a callback to be called when the Intercom chat is hidden (e.g., user closes the messenger)
  const onHide = useCallback((callback: () => void) => {
    onHideHandlersRef.current.add(callback);
  }, []);

  // Unregister a previously registered onHide callback — make sure to clean up in useEffect return
  const offHide = useCallback((callback: () => void) => {
    onHideHandlersRef.current.delete(callback);
  }, []);

  useEffect(() => {
    if (!(isAuthenticated && user)) return;

    const name = (() => {
      if (!(user.firstName && user.lastName)) return null;
      return `${user.firstName} ${user.lastName}`;
    })();

    update({
      email: user.email,
      userId: user.id ? `${COMPANY.name}-${user.id}` : null,
      company: COMPANY,
      createdAt: user.createdAt ?? null,
      name,
      phone: user.mobilePhone ?? null,
      verifiedEmail: user.confirmedAt ? 'Yes' : 'No',
      countryByIp: currentIp?.countryCode ?? null,
      profileCountry: user.country ?? null,
      groups: user.statuses
        ? user.statuses?.map(({ id }) => id).join(', ')
        : null,
      dateOfBirth: user.dateOfBirth ?? null,
      gender: user.gender ?? null,
    });
  }, [isAuthenticated, user, update, currentIp]);

  return (
    <IntercomContext.Provider value={{ boot, show, onHide, offHide }}>
      {children}
    </IntercomContext.Provider>
  );
};
